import * as Matter from 'matter-js';
import type { SceneDef } from './types';
import { createGround, groundTopY } from './util';

export const atwood: SceneDef = {
  labelKey: 'scene.atwood',
  defaultPlaying: true,
  params: [
    { id: 'massA', labelKey: 'dim.massA', min: 0.5, max: 10, step: 0.5, value: 3 },
    { id: 'massB', labelKey: 'dim.massB', min: 0.5, max: 10, step: 0.5, value: 5 },
  ],
  build({ engine, world, dims, bounds }) {
    const pulleyR = 30;
    const massSize = 40;
    const pulleyX = bounds.width / 2;
    const pulleyY = bounds.height * 0.15;
    const massAX = pulleyX - pulleyR;
    const massBX = pulleyX + pulleyR;

    const maxRopeLength = groundTopY(bounds) - pulleyY - massSize / 2 - 20;
    const ropeLength = Math.min(bounds.height * 0.42, maxRopeLength);
    const massStartY = pulleyY + ropeLength;

    // A mass cannot rise above the pulley wheel — top edge of mass meets bottom of wheel.
    const upperBoundY = pulleyY + pulleyR + massSize / 2;
    // A mass cannot sink past the ground top.
    const lowerBoundY = groundTopY(bounds) - massSize / 2;

    // mask: 0 → these bodies don't participate in Matter's collision system at all. The
    // ground / pulley bounds are enforced manually below; without this filter, Matter's
    // collision resolution pushes mass B slightly off the ground each frame, the tension
    // force then nudges it further up, gravity pulls it back, and the cycle reads as
    // elastic bouncing. Trade-off: these masses cannot be mouse-dragged.
    const collisionFilter = { category: 0x0001, mask: 0, group: 0 };

    const massA = Matter.Bodies.rectangle(massAX, massStartY, massSize, massSize, {
      mass: dims.massA!,
      friction: 0,
      frictionAir: 0,
      collisionFilter,
    });
    const massB = Matter.Bodies.rectangle(massBX, massStartY, massSize, massSize, {
      mass: dims.massB!,
      friction: 0,
      frictionAir: 0,
      collisionFilter,
    });

    function tensionMagnitudeReal(): number {
      // Newton's units (used by Matter.Body.applyForce). Atwood: T = 2·mA·mB·g / (mA+mB).
      const a = massA.mass;
      const b = massB.mass;
      return (2 * a * b * engine.gravity.y * engine.gravity.scale) / (a + b);
    }

    // Drive the Atwood dynamics manually each frame: no physics ropes (which would just
    // act as stiff springs tethering each mass to its own anchor independently). Tension
    // is applied as an upward force on both masses; the rope is purely visual.
    //
    // Once any bound is reached the rope is fully extended on that side, so both masses
    // must stop — in real Atwood, the inextensible rope locks the system. We capture both
    // positions in `locked` and pin them every frame after; Matter would otherwise keep
    // adding gravity force to the still-moving mass (zeroed velocity doesn't help —
    // Verlet integration applies `force/mass · dt²` to position each step regardless),
    // causing a slow drift toward the ground.
    let locked: { aY: number; bY: number } | null = null;

    const onBeforeUpdate = () => {
      // Lock X every frame so the masses fall straight down / rise straight up.
      if (massA.position.x !== massAX) {
        Matter.Body.setPosition(massA, { x: massAX, y: massA.position.y });
      }
      if (massB.position.x !== massBX) {
        Matter.Body.setPosition(massB, { x: massBX, y: massB.position.y });
      }
      if (massA.velocity.x !== 0) {
        Matter.Body.setVelocity(massA, { x: 0, y: massA.velocity.y });
      }
      if (massB.velocity.x !== 0) {
        Matter.Body.setVelocity(massB, { x: 0, y: massB.velocity.y });
      }

      if (locked) {
        Matter.Body.setPosition(massA, { x: massAX, y: locked.aY });
        Matter.Body.setPosition(massB, { x: massBX, y: locked.bY });
        Matter.Body.setVelocity(massA, { x: 0, y: 0 });
        Matter.Body.setVelocity(massB, { x: 0, y: 0 });
        return;
      }

      const aHi = massA.position.y <= upperBoundY;
      const aLo = massA.position.y >= lowerBoundY;
      const bHi = massB.position.y <= upperBoundY;
      const bLo = massB.position.y >= lowerBoundY;
      if (aHi || aLo || bHi || bLo) {
        // Clamp the mass that hit a bound to that bound, leave the other where it currently
        // is. Both then stay there forever (until scene rebuild / reset clears `locked`).
        const aY = aHi ? upperBoundY : aLo ? lowerBoundY : massA.position.y;
        const bY = bHi ? upperBoundY : bLo ? lowerBoundY : massB.position.y;
        locked = { aY, bY };
        Matter.Body.setPosition(massA, { x: massAX, y: aY });
        Matter.Body.setPosition(massB, { x: massBX, y: bY });
        Matter.Body.setVelocity(massA, { x: 0, y: 0 });
        Matter.Body.setVelocity(massB, { x: 0, y: 0 });
        return;
      }

      const t = tensionMagnitudeReal();
      Matter.Body.applyForce(massA, massA.position, { x: 0, y: -t });
      Matter.Body.applyForce(massB, massB.position, { x: 0, y: -t });
    };
    Matter.Events.on(engine, 'beforeUpdate', onBeforeUpdate);

    const ground = createGround(bounds);
    const bodies = [ground, massA, massB];
    Matter.World.add(world, bodies);

    return {
      bodies,
      constraints: [],
      drawOverlay(ctx) {
        ctx.save();
        // Ceiling and bracket holding the pulley.
        ctx.strokeStyle = '#888';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(pulleyX - 80, pulleyY - pulleyR - 12);
        ctx.lineTo(pulleyX + 80, pulleyY - pulleyR - 12);
        ctx.stroke();
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(pulleyX, pulleyY - pulleyR - 12);
        ctx.lineTo(pulleyX, pulleyY);
        ctx.stroke();
        // Pulley wheel
        ctx.strokeStyle = '#444';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(pulleyX, pulleyY, pulleyR, 0, Math.PI * 2);
        ctx.stroke();
        // Pulley axle
        ctx.fillStyle = '#444';
        ctx.beginPath();
        ctx.arc(pulleyX, pulleyY, 4, 0, Math.PI * 2);
        ctx.fill();
        // Visible ropes — purely cosmetic, the masses are coupled by code not constraints.
        ctx.strokeStyle = '#666';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(pulleyX - pulleyR, pulleyY);
        ctx.lineTo(massA.position.x, massA.position.y - massSize / 2);
        ctx.moveTo(pulleyX + pulleyR, pulleyY);
        ctx.lineTo(massB.position.x, massB.position.y - massSize / 2);
        ctx.stroke();
        ctx.restore();
      },
      appliedForces(body) {
        if (body !== massA && body !== massB) return [];
        // Visual scale uses the same units as gravity (mass × gravity.y, no gravity.scale)
        // so the tension arrow's length is comparable to the gravity arrow.
        const a = massA.mass;
        const b = massB.mass;
        const tShown = (2 * a * b * engine.gravity.y) / (a + b);
        return [
          {
            origin: { ...body.position },
            components: { x: 0, y: -tShown },
            labelKey: 'force.tension',
            color: '#e91e63',
          },
        ];
      },
      dispose() {
        Matter.Events.off(engine, 'beforeUpdate', onBeforeUpdate);
        Matter.World.remove(world, bodies);
      },
    };
  },
};
